import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { getEmployees } from '@/lib/employees';
import type { EmployeeListItem } from '@/types/employee';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

interface ManagerComboboxProps {
  selectedManagers: EmployeeListItem[];
  onAddManager: (manager: EmployeeListItem) => void;
  onRemoveManager: (id: string) => void;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Marks the trigger as invalid (red border + aria-invalid) — used when the field is required and empty. */
  invalid?: boolean;
  /** id of the external error message, wired to the trigger via aria-describedby when invalid. */
  errorId?: string;
}

export function ManagerCombobox({
  selectedManagers,
  onAddManager,
  onRemoveManager,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  invalid = false,
  errorId,
}: ManagerComboboxProps) {
  const formatName = useEmployeeNameFormatter();
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange! : setInternalOpen;

  const debouncedManagerSearch = useDebouncedValue(managerSearchQuery, 300);

  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', 'manager-search', debouncedManagerSearch, filterActiveOnly],
    queryFn: () =>
      getEmployees({
        search: debouncedManagerSearch || undefined,
        limit: 50,
        page: 1,
        ...(filterActiveOnly ? { status: 'active' } : {}),
      }),
    enabled: open,
  });

  const availableManagers = useMemo(() => {
    if (!employeesData?.data) return [];
    return employeesData.data;
  }, [employeesData]);

  const selectedIds = useMemo(
    () => new Set(selectedManagers.map((m) => m.id)),
    [selectedManagers]
  );

  const handleSelect = (manager: EmployeeListItem) => {
    if (selectedIds.has(manager.id)) return;
    onAddManager(manager);
    setManagerSearchQuery('');
  };

  return (
    <>
      {selectedManagers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedManagers.map((manager) => (
            <Badge
              key={manager.id}
              variant="secondary"
              className="gap-1 pr-1 h-6 text-xs font-normal"
            >
              <Avatar className="h-4 w-4">
                {manager.avatarUrl && <AvatarImage src={manager.avatarUrl} />}
                <AvatarFallback className="text-[8px]">
                  {getInitials(manager.firstName, manager.lastName)}
                </AvatarFallback>
              </Avatar>
              {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
              <button
                type="button"
                onClick={() => onRemoveManager(manager.id)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5 cursor-pointer"
                disabled={disabled}
                aria-label={`Remove ${manager.firstName} ${manager.lastName}`}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid && errorId ? errorId : undefined}
            className={cn(
              'w-full justify-between h-9 font-normal cursor-pointer',
              invalid && 'border-destructive focus-visible:ring-destructive'
            )}
            disabled={disabled}
          >
            <span className="text-muted-foreground">
              {selectedManagers.length > 0 ? 'Add another...' : 'Select manager...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              value={managerSearchQuery}
              onValueChange={setManagerSearchQuery}
            />
            <CommandList className="max-h-48">
              {employeesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableManagers.length === 0 ? (
                <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
                  {debouncedManagerSearch ? 'No results.' : 'Type to search...'}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {availableManagers.map((manager: EmployeeListItem) => {
                    const isSelected = selectedIds.has(manager.id);
                    return (
                      <CommandItem
                        key={manager.id}
                        value={manager.id}
                        onSelect={() => handleSelect(manager)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Avatar className="h-6 w-6 mr-2">
                          {manager.avatarUrl && <AvatarImage src={manager.avatarUrl} />}
                          <AvatarFallback className="text-[10px]">
                            {getInitials(manager.firstName, manager.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">
                            {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
                          </span>
                          {manager.position?.title && (
                            <span className="text-xs text-muted-foreground truncate">
                              {manager.position.title}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
            <div className="border-t p-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={filterActiveOnly}
                  onCheckedChange={(checked) => setFilterActiveOnly(checked === true)}
                  className="h-3.5 w-3.5"
                />
                Active only
              </label>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
